import { Outlet, useLocation } from "react-router-dom";
import { TopNav } from "@frontend/components/top-nav";
import { CollabInboxSync } from "@frontend/components/collab/collab-inbox-sync";
import { WorkspaceSideGate } from "@frontend/components/workspace-side-gate";
import type { CollabListRow } from "@creators/backend/lib/collabs/queries";
import { collabInboxRevision } from "@creators/backend/lib/collabs/revision";
import { clientApi } from "@frontend/lib/api";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { useAsync } from "@frontend/lib/use-async";
import { useViewer } from "@frontend/lib/viewer";
/**
 * Collab rooms and checkout — link holders welcome, full nav when signed in.
 */
export function CollabLayout() {
  const { viewer } = useViewer();
  const location = useLocation();
  const onRoom = /^\/collabs\/[0-9a-f-]{36}/i.test(location.pathname);

  const inbox = useAsync(
    async () => {
      if (!viewer || viewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs").catch(() => ({
        collabs: [] as CollabListRow[],
      }));
      return collabs;
    },
    [viewer?.sessionId, viewer?.isAdmin, viewer?.creator?.id],
    { cacheKey: collabsCacheKey(viewer?.sessionId) },
  );

  const inboxRevision =
    viewer && !viewer.isAdmin && inbox.data
      ? collabInboxRevision(inbox.data)
      : "[]";

  const body = (
    <div className="flex h-dvh flex-col bg-canvas">
      <TopNav viewer={viewer} />
      {/* Room page has its own sync — stacking inbox sync there caused a poll storm. */}
      {viewer && !viewer.isAdmin && !onRoom ? (
        <CollabInboxSync revision={inboxRevision} onChange={inbox.reload} />
      ) : null}
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </main>
    </div>
  );

  // Same global Creator/Collaborator preference as Overview / History — collabs
  // sit outside WorkspaceLayout, so the gate has to wrap here too.
  if (viewer && !viewer.isAdmin) {
    return <WorkspaceSideGate viewer={viewer}>{body}</WorkspaceSideGate>;
  }

  return body;
}
